
import { 
  Card, 
  CardContent
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { DollarSign, TrendingUp, TrendingDown } from "lucide-react";

interface PriceElasticityCardProps {
  item: {
    id: number;
    name: string;
    category: string;
    currentPrice: number;
    cost: number;
    elasticity: number;
    optimalPrice: number;
    projectedSales: number;
    projectedRevenue: number;
    competitiveIndex: number;
  };
}

export const PriceElasticityCard = ({ item }: PriceElasticityCardProps) => {
  const isPriceIncrease = item.optimalPrice > item.currentPrice;
  const difference = Math.abs(((item.optimalPrice - item.currentPrice) / item.currentPrice) * 100).toFixed(1);
  const margin = ((item.currentPrice - item.cost) / item.currentPrice) * 100;
  const optimalMargin = ((item.optimalPrice - item.cost) / item.optimalPrice) * 100;
  const marginDifference = optimalMargin - margin;
  
  return (
    <Card className="overflow-hidden h-full">
      <div className={`h-2 ${isPriceIncrease ? 'bg-green-500' : 'bg-blue-500'}`} />
      <CardContent className="p-4">
        <div className="space-y-4">
          <div className="flex justify-between items-start">
            <div>
              <h3 className="font-semibold text-lg">{item.name}</h3>
              <p className="text-muted-foreground text-sm">{item.category}</p>
            </div>
            <Badge className={isPriceIncrease ? 'bg-green-500' : 'bg-blue-500'}>
              {isPriceIncrease ? (
                <TrendingUp className="mr-1 h-3 w-3" />
              ) : (
                <TrendingDown className="mr-1 h-3 w-3" />
              )}
              {difference}%
            </Badge>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Current Price</p>
              <p className="font-semibold">${item.currentPrice.toFixed(2)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Optimal Price</p>
              <p className="font-semibold">${item.optimalPrice.toFixed(2)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Elasticity</p>
              <p className="font-semibold">{item.elasticity.toFixed(1)}</p>
            </div>
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Projected Revenue</p>
              <p className="font-semibold">${item.projectedRevenue.toFixed(2)}</p>
            </div>
          </div>

          <div className="space-y-1">
            <p className="text-xs text-muted-foreground">Margin Impact</p>
            <div className="flex items-center">
              <DollarSign className="h-4 w-4 mr-1 text-muted-foreground" />
              <span className={marginDifference > 0 ? 'text-green-600' : 'text-red-500'}>
                {marginDifference > 0 ? '+' : ''}{marginDifference.toFixed(1)}%
              </span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
